import { KeyValue } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  computed,
  effect,
  forwardRef,
  input,
  signal,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WidgetConfigAccessor } from '../services/widget-config.accessor';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  Constants,
  DatePickerTypes,
  DropdownMenuDirection,
  InputLabelOptions,
  TypographyKeys,
} from '@finxone-platform/shared/sys-config-types';
import { Subject, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import { InputValidationFactory } from '../input-field-validation/input-validations-factory';
import { ValidationConfig } from '../input-field-validation/validation-config.type';

@Component({
  standalone: false,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'finxone-datepicker',
  templateUrl: './finxone-datepicker.component.html',
  styleUrls: ['./finxone-datepicker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxoneDatePickerComponent),
      multi: true,
    },
  ],
})
export class FinxoneDatePickerComponent
  extends InputValidationFactory
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, OnDestroy
{
  private readonly cd = inject(ChangeDetectorRef);
  private readonly configService = inject(WidgetConfigAccessor);

  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() public placeholder = '';
  @Input() public label = '';
  @Input() public inputMode = 'date';
  @Input() public class = '';
  @Input() public readOnly = false;
  @Input() public isDisabled = false;
  @Input() public formControlInvalid = false;
  @Input() public globalCssVariables: Record<string, string> = {};
  @Input() public customDateInputTemplate?: TemplateRef<unknown>;
  @Input() public customErrorTemplate?: TemplateRef<unknown>;
  @Input() public id = '';
  @Input() public name = '';
  @Input() public value = '';
  @Input() public inputLabelTooltipText = '';
  @Input() public minDate: string;
  @Input() public maxDate: string;
  @Input() public dateFormat? = DatePickerTypes.DATEPICKER;
  @Input() public dayFormControl: FormControl;
  @Input() public monthFormControl: FormControl;
  @Input() public yearFormControl: FormControl;
  @Input() public helperText: string;
  @Input() public autoCloseOnSelect = true;

  // Month dropdown config, only applies when dateFormat is DatePickerTypes.INPUT
  @Input() public isScrollable = true;
  @Input() public isScrollPeekEnabled = false;
  @Input() public visibleItemsCount = 5;
  @Input() public menuDirection = DropdownMenuDirection.AUTO;

  public spaceBetweenElementUnit = input('');
  public spaceBetweenElementValue = input(0);

  protected readonly spaceBetweenItem = computed(() => {
    return `${this.spaceBetweenElementValue()}${this.spaceBetweenElementUnit()}`;
  });

  // Validation Configuration
  @Input() validationConfig: ValidationConfig[] = [];
  @Input() showErrors = true;

  // Internal form control
  public readonly control = new FormControl();

  // Used for ControlValueAccessor
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: Function = () => {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  onTouched: Function = () => {};

  public labelType: InputLabelOptions;
  public labelTypeOptions = InputLabelOptions;
  public textStylingClass: TypographyKeys;
  public borderRadiusClass = '';
  private cssVars: Record<string, string> = {};

  public isOpen = signal(false);
  public date = signal<Date | undefined>(undefined);

  public readonly resolvedMinDate = signal<Date | undefined>(undefined);
  public readonly resolvedMaxDate = signal<Date | undefined>(undefined);

  public readonly DatePickerTypes = DatePickerTypes;
  public readonly allMonths = Constants.allMonths;
  public selectedMonth: string;
  public tooltipBorderRadiusClass = '';
  public tooltipTextStyleClass = '';

  constructor() {
    super();
    effect(() => {
      this.getInputStyles();
    });
  }

  ngOnInit() {
    this.control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cd.markForCheck();
    });

    if (this.dateFormat === DatePickerTypes.INPUT) {
      // Subscribe to day/month/year changes and update control value
      if (this.dayFormControl) {
        this.dayFormControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.updateCombinedDate();
        });
      }
      if (this.monthFormControl) {
        this.monthFormControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
          // Update the selected month display when month value changes
          if (value != null && value !== '') {
            const monthKey = +value;
            this.selectedMonth = this.allMonths.find((month) => month.key === monthKey)?.name || '';
          } else {
            this.selectedMonth = '';
          }
          this.updateCombinedDate();
        });
      }
      if (this.yearFormControl) {
        this.yearFormControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.updateCombinedDate();
        });
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.monthFormControl) {
      const value = this.monthFormControl.value;
      if (value != null && value !== '') {
        const monthKey = +value;
        this.selectedMonth = this.allMonths.find((month) => month.key === monthKey)?.name || '';
      }
    }
  }

  private getInputStyles() {
    const globalCssVariables = this.configService.globalCssVariables();
    if (globalCssVariables) {
      this.cssVars = globalCssVariables;
      // get the label type and text style class from root styles
      this.labelType = this.getCssVariableValue('--inputFieldLabelType') as InputLabelOptions;
      this.textStylingClass = this.getCssVariableValue('--inputFieldTextStylingClass') as TypographyKeys;
      this.borderRadiusClass = this.getCssVariableValue('--inputFieldBorderRadiusClass');
      this.tooltipTextStyleClass = this.getCssVariableValue('--tooltipTextStyleClass');
      this.tooltipBorderRadiusClass = this.getCssVariableValue('--tooltipBorderRadiusClass');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']?.currentValue !== undefined) {
      this.control.setValue(changes['value']?.currentValue);
    }
    if (changes['validationConfig']?.currentValue !== undefined) {
      const validators = this.setupValidation(this.validationConfig);
      this.control.setValidators(validators);
      this.control.updateValueAndValidity();
    }
    if (changes['formControlInvalid']?.currentValue !== undefined) {
      this.setFormControlValidity(this.formControlInvalid);
    }
    if (changes['minDate']) {
      this.resolvedMinDate.set(this.parseDateValue(this.minDate));
    }
    if (changes['maxDate']) {
      this.resolvedMaxDate.set(this.parseDateValue(this.maxDate));
    }
  }

  onChangeDateValue(selectedDate: Date) {
    this.onChange(selectedDate);
    this.cd.markForCheck();
    this.control.setValue(selectedDate);
  }

  private setFormControlValidity(formControlInvalid: boolean) {
    if (!formControlInvalid) {
      this.control.markAsPristine();
      this.control.markAsUntouched();
    } else {
      this.control.markAsDirty();
      this.control.markAsTouched();
    }
  }

  public writeValue(value: string): void {
    this.value = value;
    if (value) {
      this.date.set(new Date(value));
    }
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public registerOnChange(fn: Function): void {
    this.onChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    isDisabled ? this.control.disable() : this.control.enable();
  }

  public onInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.onChange(value);
    this.cd.markForCheck();
  }

  private getCssVariableValue(variableName: string): string {
    return this.cssVars[variableName];
  }

  public getErrorMessageBasedOnKey(error: KeyValue<string, any>): string {
    const key = error.key;
    if (!this.validationConfig.some((config) => config.type === key) && typeof error.value === 'string') {
      return error.value;
    } else {
      const matchingConfig = this.validationConfig.find((config) => config.type === key);
      if (matchingConfig) {
        return this.getErrorMessage(matchingConfig);
      }
    }
    return 'Cannot find error message';
  }

  public parseDateValue(value: string | undefined): Date | undefined {
    if (!value) {
      return undefined;
    }

    const trimmedValue = value.trim();
    const wildcard = trimmedValue.toLowerCase();
    switch (wildcard) {
      case 'today':
        return dayjs().startOf('day').toDate();
      case 'yesterday':
        return dayjs().subtract(1, 'day').startOf('day').toDate();
      case 'tomorrow':
        return dayjs().add(1, 'day').startOf('day').toDate();
    }

    // Reject arbitrary numeric-only strings (e.g. '0', '100'), which native/dayjs
    // parsing can otherwise misinterpret as a valid date (e.g. a year or timestamp).
    if (/^\d+$/.test(trimmedValue)) {
      return undefined;
    }

    const date = dayjs(trimmedValue);
    return date.isValid() ? date.toDate() : undefined;
  }

  public handleLabelClick(): void {
    this.isOpen.set(true);
    this.updateOverlayDynamicStyles(true);
  }

  private updateCombinedDate() {
    const day = this.dayFormControl?.value;
    const month = this.monthFormControl?.value;
    const year = this.yearFormControl?.value;
    // month is 0-11 (January = 0), so truthiness checks would wrongly treat it as empty
    const isPresent = (value: unknown) => value !== null && value !== undefined && value !== '';

    if (isPresent(day) || isPresent(month) || isPresent(year)) {
      // Only update if at least one is populated
      if (isPresent(day) && isPresent(month) && isPresent(year)) {
        // Only construct date if year has 4 digits to prevent partial year values like "20" being interpreted as year 20 CE
        if (year.toString().length === 4) {
          // All parts present, create date
          const date = new Date(year, month, day);
          if (!isNaN(date.getTime())) {
            this.control.setValue(date);
            this.onChange(date);
          }
        } else {
          // Year incomplete, clear control value
          this.control.setValue(null);
          this.onChange(null);
        }
      } else {
        // Partial date, clear control value
        this.control.setValue(null);
        this.onChange(null);
      }
      this.cd.markForCheck();
    }
  }

  // Called by signal effect
  private updateOverlayDynamicStyles(isOpen: boolean) {
    const overlayContainer = document.querySelector('.cdk-overlay-container') as HTMLElement;
    if (!overlayContainer) return;
    overlayContainer.style.zIndex = isOpen ? '100001' : '';
  }

  ngOnDestroy(): void {
    this.isOpen.set(false);
    // Cleanup dynamic style
    this.updateOverlayDynamicStyles(false);
  }
}
